/**
 * مُقلِع gtag المشترك (GA4 + Google Ads) — نسخة واحدة من السكربت.
 *
 * القواعد:
 *  • التحليلات والإعلانات موافقتان مستقلّتان: موافقة الإعلانات وحدها تُحمّل
 *    الوسم لـ Google Ads فقط، ولا تُشغّل GA4 إطلاقاً.
 *  • `analytics_storage` لا يُمنَح إلّا بموافقة التحليلات الصريحة.
 *  • لا سكربت مكرّر ولا `config` مكرّر لنفس المعرّف.
 *  • الجاهزيّة تُعلَن بحدث حتّى تُعيد المستهلكات المحاولة بلا polling.
 *  • تحميل lazy: الاستدعاء يبقى بيد المُستدعي (idle / بعد الموافقة).
 */

import { GA4_MEASUREMENT_ID } from "./ga4-config";
import { GOOGLE_ADS_ID } from "./googleAdsConfig";
import { analyticsUrl } from "./analyticsUrl";
import { hasAdsConsent, hasAnalyticsConsent } from "./cookie-consent";

export const GOOGLE_TAG_READY_EVENT = "lecmo:google-tag-ready";

type GtagWindow = typeof window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

let scriptStarted = false;
let tagReady = false;
let analyticsConfigured = false;

function w(): GtagWindow {
  return window as GtagWindow;
}

function installShim(): void {
  const win = w();
  win.dataLayer = win.dataLayer || [];
  if (typeof win.gtag !== "function") {
    // Google يستهلك كائن Arguments الذي يدفعه snippet الرسميّ — لا مصفوفة.
    win.gtag = function (..._args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer?.push(arguments);
    };
  }
}

/** هل السكربت الرسميّ جاهز فعلاً (لا مجرّد الطابور)؟ */
export function isGoogleTagReady(): boolean {
  return tagReady;
}

/** يستدعي `cb` فوراً إن كان الوسم جاهزاً، وإلّا عند الجاهزيّة. يعيد دالّة إلغاء. */
export function onGoogleTagReady(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  if (tagReady) {
    cb();
    return () => {};
  }
  const handler = () => cb();
  window.addEventListener(GOOGLE_TAG_READY_EVENT, handler);
  return () => window.removeEventListener(GOOGLE_TAG_READY_EVENT, handler);
}

function markReady(): void {
  if (tagReady) return;
  tagReady = true;
  window.dispatchEvent(new CustomEvent(GOOGLE_TAG_READY_EVENT));
}

function configureAnalytics(): void {
  if (analyticsConfigured || !hasAnalyticsConsent()) return;
  analyticsConfigured = true;
  w().gtag?.("config", GA4_MEASUREMENT_ID, {
    page_location: analyticsUrl(window.location.href),
    page_referrer: analyticsUrl(document.referrer || ""),
  });
}

/**
 * يحمّل الوسم مرّة واحدة حسب الموافقة الحاليّة.
 * بلا أيّ موافقة اختياريّة → لا شيء يُحمَّل.
 */
export function ensureGoogleTagLoaded(): void {
  if (typeof window === "undefined") return;
  const analytics = hasAnalyticsConsent();
  const ads = hasAdsConsent();
  if (!analytics && !ads) return;

  if (scriptStarted) {
    // موافقة التحليلات وصلت بعد أن حُمّل الوسم لأجل الإعلانات فقط.
    installShim();
    w().gtag?.("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: ads ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads ? "granted" : "denied",
    });
    if (analytics) configureAnalytics();
    return;
  }
  scriptStarted = true;

  installShim();
  const gtag = w().gtag!;
  gtag("consent", "default", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
  });
  gtag("js", new Date());
  if (analytics) configureAnalytics();

  // معرّف السكربت: GA4 عند موافقة التحليلات، وإلّا Google Ads وحده.
  const primaryId = analytics ? GA4_MEASUREMENT_ID : GOOGLE_ADS_ID;
  if (!primaryId) {
    scriptStarted = false;
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  script.addEventListener("load", markReady);
  script.addEventListener("error", () => {
    // فشل الشبكة: نُبقي `scriptStarted` حتّى لا نُغرِق الصفحة بمحاولات،
    // ولا نُعلن الجاهزيّة لأنّ أيّ حدث سيضيع.
  });
  document.head.appendChild(script);
}

/** اختبارات فقط: يعيد الحالة الداخليّة إلى الصفر. */
export function __resetGoogleTagBootstrapForTests(): void {
  scriptStarted = false;
  tagReady = false;
  analyticsConfigured = false;
}
